// ./src/components/features/Travels/TravelWaypointsDrawer/WaypointsList.tsx
import React, { useCallback } from "react";
import {
  Stack,
  Group,
  Text,
  Box,
  Paper,
  Badge,
  ActionIcon,
  Collapse,
  UnstyledButton,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Expense } from "@/utils/api";
import { AppIcon, WeatherDisplay } from "@/components/generic";
import { EXPENSE_TYPES } from "@/config";
import { PlaceTypeBadge } from "@/components/features";
import {
  formatDateTime,
  formatDurationBetween,
  calculateOdometerDistance,
} from "@/utils/dateHelpers";

// ============================================
// Types
// ============================================

export interface WaypointsListProps {
  /** Grouped expenses by type */
  groupedExpenses: GroupedExpenses;

  /** Expanded state for each section */
  expandedSections: ExpandedSections;

  /** Toggle section expanded state */
  onToggleSection: (section: keyof ExpandedSections) => void;

  /** Currently hovered waypoint ID (from map or list) */
  hoveredWaypointId: string | null;

  /** Callback when a waypoint is hovered */
  onWaypointHover: (waypointId: string | null) => void;

  /** Callback when edit button is clicked */
  onEditWaypoint: (waypoint: Expense) => void;

  /** Distance unit label */
  distanceLabel: string;

  /** Volume unit label */
  volumeLabel: string;

  /** Currency symbol */
  currencySymbol: string;

  /** Optional: scroll to a specific waypoint */
  scrollToWaypointId?: string | null;
}

export interface GroupedExpenses {
  waypoints: Expense[];
  refuels: Expense[];
  expenses: Expense[];
  checkpoints: Expense[];
  revenues: Expense[];
}

export interface ExpandedSections {
  waypoints: boolean;
  refuels: boolean;
  expenses: boolean;
  checkpoints: boolean;
  revenues: boolean;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Check if record has weather data
 */
const hasWeatherData = (record: Expense): boolean => {
  return record.weatherTempC !== null && record.weatherTempC !== undefined;
};

// ============================================
// Section Header Component
// ============================================

interface SectionHeaderProps {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  icon: string;
  color?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  count,
  expanded,
  onToggle,
  icon,
  color = "gray",
}) => (
  <UnstyledButton onClick={onToggle} w="100%">
    <Group justify="space-between" py="xs">
      <Group gap="xs">
        <AppIcon iconName={icon} size={18} color={`var(--mantine-color-${color}-6)`} />
        <Text fw={600} size="sm">
          {title}
        </Text>
        <Badge size="sm" variant="light" color={color}>
          {count}
        </Badge>
      </Group>
      <AppIcon iconName={expanded ? "ChevronUp" : "ChevronDown"} size={16} />
    </Group>
  </UnstyledButton>
);

// ============================================
// Waypoint Connector Component
// ============================================

interface WaypointConnectorProps {
  duration: string | null;
  distance: number | null;
  distanceLabel: string;
}

const WaypointConnector: React.FC<WaypointConnectorProps> = ({
  duration,
  distance,
  distanceLabel,
}) => {
  const { t } = useTranslation(["travels", "common"]);

  // Don't render if we have no data to show
  if (!duration && !distance) return null;

  return (
    <Group gap={6} pl="md" py={4} wrap="nowrap">
      {/* Vertical connector line */}
      <Box
        style={{
          width: 2,
          height: 24,
          backgroundColor: "var(--mantine-color-gray-3)",
          borderRadius: 1,
          marginLeft: 6,
        }}
      />

      {/* Duration and distance info */}
      <Group gap="xs" wrap="nowrap">
        {duration && (
          <Group gap={4} wrap="nowrap">
            <AppIcon iconName="Clock" size={12} color="var(--mantine-color-gray-5)" />
            <Text size="xs" c="dimmed">
              {duration}
            </Text>
          </Group>
        )}

        {duration && distance && (
          <Text size="xs" c="dimmed">
            •
          </Text>
        )}

        {distance && (
          <Group gap={4} wrap="nowrap">
            <AppIcon iconName="Route" size={12} color="var(--mantine-color-gray-5)" />
            <Text size="xs" c="dimmed">
              {distance.toLocaleString()} {t(`common:${distanceLabel}`)}
            </Text>
          </Group>
        )}
      </Group>
    </Group>
  );
};

// ============================================
// Waypoint Card Component
// ============================================

interface WaypointCardProps {
  waypoint: Expense;
  index: number;
  total: number;
  distanceLabel: string;
  onEdit: (waypoint: Expense) => void;
  onHover: (waypointId: string | null) => void;
  isHighlighted: boolean;
  isFirst: boolean;
  isLast: boolean;
  /** True if odometer decreased compared to previous waypoint */
  odometerDecreased?: boolean | null;
}

const WaypointCard: React.FC<WaypointCardProps> = ({
  waypoint,
  index,
  //total,
  distanceLabel,
  onEdit,
  onHover,
  isHighlighted,
  isFirst,
  isLast,
  odometerDecreased = false,
}) => {
  const { t } = useTranslation(["travels", "common"]);

  const hasWeather = hasWeatherData(waypoint);

  const getPositionLabel = (): string => {
    if (isFirst) return t("travels:waypoints.labels.start");
    if (isLast) return t("travels:waypoints.labels.end");
    return t("travels:waypoints.labels.stop", { number: index });
  };

  const handleMouseEnter = useCallback(() => {
    onHover(waypoint.id || null);
  }, [onHover, waypoint.id]);

  const handleMouseLeave = useCallback(() => {
    onHover(null);
  }, [onHover]);

  return (
    <Paper
      p="sm"
      withBorder
      data-waypoint-id={waypoint.id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        borderLeft: `3px solid var(--mantine-color-${isFirst ? "green" : isLast ? "red" : "blue"}-5)`,
        backgroundColor: isHighlighted ? "var(--mantine-color-blue-0)" : undefined,
        transition: "background-color 0.15s ease",
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          {/* Position and Time */}
          <Group gap="xs" wrap="nowrap">
            <Badge
              size="sm"
              variant="light"
              color={isFirst ? "green" : isLast ? "red" : "blue"}
            >
              {getPositionLabel()}
            </Badge>
            <Text size="xs" c="dimmed">
              {formatDateTime(waypoint.whenDone, "timeShort")}
            </Text>
          </Group>

          {/* Location Type */}
          <PlaceTypeBadge value={waypoint.pointType} size="xs" />

          {/* Place Name */}
          {waypoint.whereDone && (
            <Text size="sm" fw={500} lineClamp={1}>
              {waypoint.whereDone}
            </Text>
          )}

          {/* Address */}
          {waypoint.location && (
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName="MapPin" size={14} color="var(--mantine-color-gray-5)" />
              <Text size="xs" c="dimmed" lineClamp={1}>
                {waypoint.location}
              </Text>
            </Group>
          )}

          {/* Odometer */}
          {waypoint.odometer !== undefined && waypoint.odometer !== null && (
            <Group gap={4}>
              <AppIcon
                iconName="Gauge"
                size={14}
                color={odometerDecreased ? "var(--mantine-color-orange-6)" : "var(--mantine-color-gray-5)"}
              />
              <Text
                size="xs"
                c={odometerDecreased ? "orange.6" : "dimmed"}
                fw={odometerDecreased ? 500 : undefined}
              >
                {waypoint.odometer} {t(`common:${distanceLabel}`)}
              </Text>
              {odometerDecreased && (
                <Tooltip
                  label={t("travels:waypoints.warnings.odometerDecreased", {
                    defaultValue: "Odometer reading is lower than the previous waypoint",
                  })}
                  withArrow
                  multiline
                  w={200}
                >
                  <Box component="span" style={{ display: "inline-flex", cursor: "help" }}>
                    <AppIcon
                      iconName="AlertTriangle"
                      size={14}
                      color="var(--mantine-color-orange-6)"
                    />
                  </Box>
                </Tooltip>
              )}
            </Group>
          )}

          {/* Weather */}
          {hasWeather && (
            <WeatherDisplay
              data={waypoint}
              variant="compact"
            />
          )}

          {/* Comments */}
          {waypoint.comments && (
            <Text size="xs" c="dimmed" fs="italic" lineClamp={2}>
              {waypoint.comments}
            </Text>
          )}
        </Stack>

        {/* Edit button */}
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => onEdit(waypoint)}
          style={{ flexShrink: 0 }}
        >
          <AppIcon iconName="Pencil" size={16} />
        </ActionIcon>
      </Group>
    </Paper>
  );
};

// ============================================
// Expense Card Component (Read-only)
// ============================================

interface ExpenseCardProps {
  expense: Expense;
  distanceLabel: string;
  volumeLabel: string;
  currencySymbol: string;
  onHover: (waypointId: string | null) => void;
  isHighlighted: boolean;
}

const ExpenseCard: React.FC<ExpenseCardProps> = ({
  expense,
  //distanceLabel,
  volumeLabel,
  currencySymbol,
  onHover,
  isHighlighted,
}) => {
  const hasWeather = hasWeatherData(expense);

  const getExpenseIcon = (): string => {
    switch (expense.expenseType) {
      case EXPENSE_TYPES.REFUEL:
        return "GasStation";
      case EXPENSE_TYPES.EXPENSE:
        return "Receipt";
      case EXPENSE_TYPES.CHECKPOINT:
        return "Flag";
      case EXPENSE_TYPES.REVENUE:
        return "CurrencyDollar";
      default:
        return "Receipt";
    }
  };

  const getExpenseColor = (): string => {
    switch (expense.expenseType) {
      case EXPENSE_TYPES.REFUEL:
        return "orange";
      case EXPENSE_TYPES.EXPENSE:
        return "red";
      case EXPENSE_TYPES.CHECKPOINT:
        return "gray";
      case EXPENSE_TYPES.REVENUE:
        return "green";
      default:
        return "gray";
    }
  };

  const handleMouseEnter = useCallback(() => {
    onHover(expense.id || null);
  }, [onHover, expense.id]);

  const handleMouseLeave = useCallback(() => {
    onHover(null);
  }, [onHover]);

  return (
    <Paper
      p="sm"
      withBorder
      data-waypoint-id={expense.id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        backgroundColor: isHighlighted ? "var(--mantine-color-blue-0)" : undefined,
        transition: "background-color 0.15s ease",
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Box
            p={6}
            style={{
              backgroundColor: `var(--mantine-color-${getExpenseColor()}-0)`,
              borderRadius: "var(--mantine-radius-sm)",
              flexShrink: 0,
            }}
          >
            <AppIcon
              iconName={getExpenseIcon()}
              size={18}
              color={`var(--mantine-color-${getExpenseColor()}-6)`}
            />
          </Box>

          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" fw={500} lineClamp={1}>
                {expense.shortNote || expense.expenseKind?.name || expense.whereDone || "—"}
              </Text>
            </Group>

            <Text size="xs" c="dimmed">
              {formatDateTime(expense.whenDone, "datetime")}
            </Text>

            {/* Refuel specific info */}
            {expense.expenseType === EXPENSE_TYPES.REFUEL && expense.refuelVolume && (
              <Text size="xs" c="dimmed">
                {expense.refuelVolume.toFixed(2)} {volumeLabel}
                {expense.isFullTank && " • Full tank"}
              </Text>
            )}

            {/* Weather */}
            {hasWeather && (
              <WeatherDisplay
                data={expense}
                variant="compact"
              />
            )}
          </Stack>
        </Group>

        {/* Amount */}
        {expense.totalPrice !== undefined && expense.totalPrice !== null && (
          <Text
            size="sm"
            fw={600}
            c={expense.expenseType === EXPENSE_TYPES.REVENUE ? "green" : "red"}
            style={{ flexShrink: 0 }}
          >
            {expense.expenseType === EXPENSE_TYPES.REVENUE ? "+" : "-"}
            {currencySymbol}
            {expense.totalPrice.toFixed(2)}
          </Text>
        )}
      </Group>
    </Paper>
  );
};

// ============================================
// Main Component
// ============================================

const WaypointsListComponent: React.FC<WaypointsListProps> = ({
  groupedExpenses,
  expandedSections,
  onToggleSection,
  hoveredWaypointId,
  onWaypointHover,
  onEditWaypoint,
  distanceLabel,
  volumeLabel,
  currencySymbol,
}) => {
  const { t } = useTranslation(["travels", "common"]);

  // Check if we have any data in each section
  const hasWaypoints = groupedExpenses.waypoints.length > 0;
  const hasRefuels = groupedExpenses.refuels.length > 0;
  const hasExpenses = groupedExpenses.expenses.length > 0;
  const hasCheckpoints = groupedExpenses.checkpoints.length > 0;
  const hasRevenues = groupedExpenses.revenues.length > 0;
  const hasAnyRelatedExpenses = hasRefuels || hasExpenses || hasCheckpoints || hasRevenues;

  return (
    <Stack gap="md">
      {/* Waypoints Section */}
      <SectionHeader
        title={t("travels:waypoints.sections.waypoints")}
        count={groupedExpenses.waypoints.length}
        expanded={expandedSections.waypoints}
        onToggle={() => onToggleSection("waypoints")}
        icon="Route"
        color="blue"
      />
      <Collapse in={expandedSections.waypoints}>
        {hasWaypoints ? (
          <Stack gap={0}>
            {groupedExpenses.waypoints.map((waypoint, index) => {
              const prevWaypoint = index > 0 ? groupedExpenses.waypoints[index - 1] : null;
              const duration = prevWaypoint
                ? formatDurationBetween(prevWaypoint.whenDone, waypoint.whenDone)
                : null;
              const distance = prevWaypoint
                ? calculateOdometerDistance(prevWaypoint.odometer, waypoint.odometer)
                : null;

              // Check if odometer decreased compared to previous waypoint
              const odometerDecreased =
                prevWaypoint &&
                prevWaypoint.odometer !== undefined &&
                prevWaypoint.odometer !== null &&
                waypoint.odometer !== undefined &&
                waypoint.odometer !== null &&
                waypoint.odometer < prevWaypoint.odometer;

              return (
                <React.Fragment key={waypoint.id}>
                  {/* Connector showing time/distance from previous waypoint */}
                  {index > 0 && (
                    <WaypointConnector
                      duration={duration}
                      distance={distance}
                      distanceLabel={distanceLabel}
                    />
                  )}

                  {/* Waypoint card */}
                  <WaypointCard
                    waypoint={waypoint}
                    index={index}
                    total={groupedExpenses.waypoints.length}
                    distanceLabel={distanceLabel}
                    onEdit={onEditWaypoint}
                    onHover={onWaypointHover}
                    isHighlighted={waypoint.id === hoveredWaypointId}
                    isFirst={index === 0}
                    isLast={index === groupedExpenses.waypoints.length - 1}
                    odometerDecreased={odometerDecreased}
                  />
                </React.Fragment>
              );
            })}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {t("travels:waypoints.empty.waypoints")}
          </Text>
        )}
      </Collapse>

      {/* Related Expenses */}
      {hasAnyRelatedExpenses && (
        <>
          {/* Refuels */}
          {hasRefuels && (
            <>
              <SectionHeader
                title={t("travels:waypoints.sections.refuels")}
                count={groupedExpenses.refuels.length}
                expanded={expandedSections.refuels}
                onToggle={() => onToggleSection("refuels")}
                icon="GasStation"
                color="orange"
              />
              <Collapse in={expandedSections.refuels}>
                <Stack gap="sm">
                  {groupedExpenses.refuels.map((expense) => (
                    <ExpenseCard
                      key={expense.id}
                      expense={expense}
                      distanceLabel={distanceLabel}
                      volumeLabel={volumeLabel}
                      currencySymbol={currencySymbol}
                      onHover={onWaypointHover}
                      isHighlighted={expense.id === hoveredWaypointId}
                    />
                  ))}
                </Stack>
              </Collapse>
            </>
          )}

          {/* Expenses */}
          {hasExpenses && (
            <>
              <SectionHeader
                title={t("travels:waypoints.sections.expenses")}
                count={groupedExpenses.expenses.length}
                expanded={expandedSections.expenses}
                onToggle={() => onToggleSection("expenses")}
                icon="Receipt"
                color="red"
              />
              <Collapse in={expandedSections.expenses}>
                <Stack gap="sm">
                  {groupedExpenses.expenses.map((expense) => (
                    <ExpenseCard
                      key={expense.id}
                      expense={expense}
                      distanceLabel={distanceLabel}
                      volumeLabel={volumeLabel}
                      currencySymbol={currencySymbol}
                      onHover={onWaypointHover}
                      isHighlighted={expense.id === hoveredWaypointId}
                    />
                  ))}
                </Stack>
              </Collapse>
            </>
          )}

          {/* Checkpoints */}
          {hasCheckpoints && (
            <>
              <SectionHeader
                title={t("travels:waypoints.sections.checkpoints")}
                count={groupedExpenses.checkpoints.length}
                expanded={expandedSections.checkpoints}
                onToggle={() => onToggleSection("checkpoints")}
                icon="Flag"
                color="gray"
              />
              <Collapse in={expandedSections.checkpoints}>
                <Stack gap="sm">
                  {groupedExpenses.checkpoints.map((expense) => (
                    <ExpenseCard
                      key={expense.id}
                      expense={expense}
                      distanceLabel={distanceLabel}
                      volumeLabel={volumeLabel}
                      currencySymbol={currencySymbol}
                      onHover={onWaypointHover}
                      isHighlighted={expense.id === hoveredWaypointId}
                    />
                  ))}
                </Stack>
              </Collapse>
            </>
          )}

          {/* Revenues */}
          {hasRevenues && (
            <>
              <SectionHeader
                title={t("travels:waypoints.sections.revenues")}
                count={groupedExpenses.revenues.length}
                expanded={expandedSections.revenues}
                onToggle={() => onToggleSection("revenues")}
                icon="CurrencyDollar"
                color="green"
              />
              <Collapse in={expandedSections.revenues}>
                <Stack gap="sm">
                  {groupedExpenses.revenues.map((expense) => (
                    <ExpenseCard
                      key={expense.id}
                      expense={expense}
                      distanceLabel={distanceLabel}
                      volumeLabel={volumeLabel}
                      currencySymbol={currencySymbol}
                      onHover={onWaypointHover}
                      isHighlighted={expense.id === hoveredWaypointId}
                    />
                  ))}
                </Stack>
              </Collapse>
            </>
          )}
        </>
      )}
    </Stack>
  );
};

export const WaypointsList = React.memo(WaypointsListComponent);